import React, { useState } from "react";
import { Shield, Lock, Eye, FileText, ArrowLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface PolicySection {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("introduction");

  // Modern structured content payload
  const sections: PolicySection[] = [
    {
      id: "introduction",
      title: "1. Introduction",
      icon: <Shield className="w-4 h-4" />,
      content: (
        <>
          <p>
            Welcome to our platform. We value your trust and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our mobile applications.
          </p>
          <p className="mt-4">
            By accessing or using our services, you agree to the terms of this Privacy Policy. If you do not agree with the terms of this privacy policy, please do not access the application.
          </p>
        </>
      ),
    },
    {
      id: "data-collection",
      title: "2. Information We Collect",
      icon: <Eye className="w-4 h-4" />,
      content: (
        <>
          <p>We may collect information about you in a variety of ways. The information we may collect on the Application includes:</p>
          <ul className="list-disc pl-5 mt-3 space-y-2 text-slate-600">
            <li><strong className="text-slate-800">Personal Data:</strong> Personally identifiable information, such as your name, shipping address, email address, and telephone number, and demographic information that you voluntarily give to us.</li>
            <li><strong className="text-slate-800">Device & Derivative Data:</strong> Information our servers automatically collect when you access the Application, such as your IP address, your browser type, your operating system, your access times, and the pages you have viewed directly before and after accessing the Application.</li>
            <li><strong className="text-slate-800">Financial Data:</strong> Financial information, such as data related to your payment method (e.g. valid credit card number, card brand, expiration date) that we may collect when you purchase, order, return, exchange, or request information about our services.</li>
          </ul>
        </>
      ),
    },
    {
      id: "data-usage",
      title: "3. How We Use Your Information",
      icon: <Lock className="w-4 h-4" />,
      content: (
        <>
          <p>Having accurate information about you permits us to provide you with a smooth, efficient, and customized experience. Specifically, we may use information collected about you via the Application to:</p>
          <ul className="list-disc pl-5 mt-3 space-y-2 text-slate-600">
            <li>Create and manage your account.</li>
            <li>Process your transactions and device inspections seamlessly.</li>
            <li>Email you regarding your account or order status.</li>
            <li>Fulfill and manage purchases, orders, payments, and other transactions related to the Application.</li>
            <li>Increase the efficiency and operation of the Application.</li>
          </ul>
        </>
      ),
    },
    {
      id: "security",
      title: "4. Security of Your Data",
      icon: <FileText className="w-4 h-4" />,
      content: (
        <>
          <p>
            We use administrative, technical, and physical security measures to help protect your personal information. While we have taken reasonable steps to secure the personal information you provide to us, please be aware that despite our efforts, no security measures are perfect or impenetrable, and no method of data transmission can be guaranteed against any interception or other type of misuse.
          </p>
        </>
      ),
    },
  ];

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="pb-20 bg-[#F8FAFC] min-h-screen">
      {/* Minimal Stark Header */}
      <div className="bg-white border-b border-slate-100 mb-10">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-slate-400 hover:text-[#363C90] transition-colors mb-4 text-[11px] font-bold uppercase tracking-wider"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">
            Privacy <span className="text-[#363C90]">Policy</span>
          </h1>
          <p className="text-slate-400 text-[13px] font-medium mt-1">
            Last updated: June 10, 2026 • Read carefully to understand your rights.
          </p>
        </div>
      </div>

      {/* Main Responsive Grid Layout */}
      <div className="max-w-[1100px] mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          
          {/* Sticky Left Navigation Sidebar */}
          <aside className="w-full lg:w-[280px] lg:sticky lg:top-6 bg-white border border-slate-100 rounded-2xl p-3 space-y-1">
            <p className="text-slate-400 text-[10px] font-black uppercase tracking-wider px-3 pt-2 mb-2">Table of Contents</p>
            {sections.map((sec) => (
              <button
                key={sec.id}
                onClick={() => scrollToSection(sec.id)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-left text-xs font-bold tracking-tight transition-all ${
                  activeSection === sec.id
                    ? "bg-[#363C90]/5 text-[#363C90] border-l-4 border-l-[#363C90]"
                    : "text-slate-600 hover:bg-slate-50 border-l-4 border-l-transparent"
                }`}
              >
                <div className="flex items-center gap-2.5 truncate">
                  <span className={`${activeSection === sec.id ? "text-[#363C90]" : "text-slate-400"}`}>
                    {sec.icon}
                  </span>
                  <span className="truncate">{sec.title.split(". ")[1]}</span>
                </div>
                <ChevronRight size={14} className={`opacity-40 transition-transform ${activeSection === sec.id ? "translate-x-0.5 opacity-100" : ""}`} />
              </button>
            ))}
          </aside>

          {/* Core Content Layer */}
          <main className="flex-1 bg-white border border-slate-100 rounded-2xl p-6 md:p-8 space-y-10 shadow-sm shadow-slate-100/50">
            {sections.map((sec) => (
              <section 
                key={sec.id} 
                id={sec.id} 
                className="scroll-mt-6 border-b border-slate-100 last:border-0 pb-8 last:pb-0"
              >
                <h2 className="text-lg font-black text-slate-900 tracking-tight mb-4 flex items-center gap-2.5">
                  <span className="p-1.5 bg-slate-50 text-[#363C90] rounded-lg">
                    {sec.icon}
                  </span>
                  {sec.title}
                </h2>
                <div className="text-slate-500 text-sm leading-relaxed font-medium pl-0.5">
                  {sec.content}
                </div>
              </section>
            ))}
          </main>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;